import { RefObject, useMemo, useRef, useState } from "react";

import { SelectInput } from "@impero-com/common/components/form/fields/selectInput";
import { RHFStringInput } from "@impero-com/common/components/form/rhfFields/rhfStringInput";
import { FormSection } from "@impero-com/common/components/formSection/formSection";
import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    emitNotification,
    Divider,
    FormField,
    FormLabel,
} from "@impero-com/ui";
import * as Sentry from "@sentry/react";
import { Typography } from "antd";
import {
    Control,
    UseFormGetValues,
    UseFormSetValue,
    UseFormTrigger,
    useController,
} from "react-hook-form";

import {
    ControlWithFieldsResources,
    NotificationType,
    NotificationRecipientKind,
    EntityView,
} from "api/generated/model";
import { Field } from "components/fieldBuilder/models";
import { RHFEditableUserTagListInput } from "components/form/rhfFields/rhfEditableUserTagListPlus";
import { RHFMarkdownInput } from "components/form/rhfFields/rhfMarkdownInput";
import { RHFSelectMultipleInput } from "components/form/rhfFields/rhfSelectMultipleInput";
import { InputSkeleton } from "components/form/skeleton/inputSkeleton";
import {
    MarkdownEditorMethods,
    MarkdownSelectionRange,
} from "components/markdownEditor/types";
import { AssignmentGroupId } from "utils/controls/assignmentUtils";
import { mapAsResourceWithoutAction } from "utils/dtoMapping";
import { useAvailableActions } from "utils/hooks/useAvailableActions";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";

import {
    ControlProgramOption,
    useControlProgramsOptions,
} from "../hooks/useControlProgramsOptions";

import { ControlAssignmentsSection } from "./components/ControlAssignmentsSection";
import { ControlPlanning } from "./components/ControlPlanning";
import { ControlSchedule } from "./components/ControlSchedule";
import { ControlFormTimeline } from "./components/ControlTimeline";
import { SuggestAiDescriptionButton } from "./components/SuggestAiDescriptionButton";
import { ControlTasksInput } from "./components/tasks/ControlTasksInput";
import { DataSheetAssignmentsForControlProgramAndCurrentControl } from "./components/tasks/types";
import {
    CONTROL_ASSIGNEES_ANCHOR,
    CONTROL_DETAILS_ANCHOR,
    CONTROL_PLANNING_ANCHOR,
    CONTROL_REMINDERS_ANCHOR,
    CONTROL_SCHEDULING_ANCHOR,
    CONTROL_TASKS_ANCHOR,
    useControlViewAnchorLabels,
} from "./consts";
import { ControlViewFormSchema, getTitleSchema } from "./formSchema";
import * as theme from "./theme.css";
import {
    generateReminderDayOptions,
    sortRemindersAscending,
    sortRemindersDescending,
} from "./utils/reminderOptions";
import { useMarkdownSourceToggleMode } from "./utils/useMarkdownSourceToggleMode";

const { Link } = Typography;

export interface ControlViewFormProps {
    control: Control<ControlViewFormSchema>;
    getValues: UseFormGetValues<ControlViewFormSchema>;
    setValue: UseFormSetValue<ControlViewFormSchema>;
    trigger: UseFormTrigger<ControlViewFormSchema>;
    onAnalytics: (
        suggestionId: string,
        fields: Field<unknown, string>[]
    ) => void;
    disabled?: boolean;
    controlResources: ControlWithFieldsResources;
    controlId: string;
    dataSheetAssignments: DataSheetAssignmentsForControlProgramAndCurrentControl;
    notificationRecipients: Record<
        AssignmentGroupId,
        Partial<Record<NotificationType, NotificationRecipientKind[]>>
    >;
    initialTriggeredByControlId: string | null;
    linkedEntities?: EntityView[];
    hasFullAssignmentsAccess: boolean;
}
export function ControlViewForm({
    control,
    getValues,
    setValue,
    trigger,
    onAnalytics,
    disabled = false,
    controlResources,
    controlId,
    dataSheetAssignments,
    notificationRecipients,
    initialTriggeredByControlId,
    linkedEntities = [],
    hasFullAssignmentsAccess,
}: ControlViewFormProps) {
    const styleVariant = disabled ? "borderless" : "normal";
    const {
        userTag: { create: canCreateTag },
    } = useAvailableActions();
    const anchorLabels = useControlViewAnchorLabels();

    const { t } = useTranslation();

    const options = useMemo(() => generateReminderDayOptions(t), [t]);

    return (
        <div className={theme.controlFormViewRoot}>
            <FormSection
                id={CONTROL_DETAILS_ANCHOR}
                titleLabel={anchorLabels[CONTROL_DETAILS_ANCHOR]}
                withDivider
            >
                <RHFStringInput
                    styleVariant={styleVariant}
                    disabled={disabled}
                    label={t("CreateControl_Control_Title")}
                    placeholder={t("CreateControl_Control_Title_Placeholder")}
                    control={control}
                    name="title"
                    schema={getTitleSchema(t)}
                />

                <ControlDescriptionMarkdownInput
                    styleVariant={styleVariant}
                    disabled={disabled}
                    control={control}
                    setValue={setValue}
                />

                <ControlProgramSelect
                    disabled={disabled}
                    control={control}
                    getValues={getValues}
                    setValue={setValue}
                />

                <RHFEditableUserTagListInput
                    label={t("Control_Tags")}
                    disabled={disabled}
                    canCreateTag={canCreateTag}
                    control={control}
                    name="tagIds"
                    cascaderClassName={theme.tagListInput}
                />
            </FormSection>

            <FormSection
                id={CONTROL_ASSIGNEES_ANCHOR}
                titleLabel={anchorLabels[CONTROL_ASSIGNEES_ANCHOR]}
                withDivider
            >
                <ControlAssignmentsSection
                    control={control}
                    trigger={trigger}
                    disabled={disabled}
                    hasFullAssignmentsAccess={hasFullAssignmentsAccess}
                    linkedEntities={linkedEntities}
                    resources={{
                        userGroups: controlResources.userGroups,
                        users: controlResources.users,
                    }}
                    controlPolicies={controlResources.controlPolicies}
                    notificationRecipients={notificationRecipients}
                />
            </FormSection>

            <div className={theme.scheduleSectionContent}>
                <FormSection
                    id={CONTROL_SCHEDULING_ANCHOR}
                    titleLabel={anchorLabels[CONTROL_SCHEDULING_ANCHOR]}
                    className={theme.scheduleContent}
                    withDivider
                >
                    <ControlSchedule
                        id="control-schedule"
                        control={control}
                        getValues={getValues}
                        setValue={setValue}
                        disabled={disabled}
                        initialCalendars={controlResources.calendars}
                        initialFinancialYear={
                            controlResources.financialYears[0] ?? null
                        }
                        controlId={controlId}
                        initialTriggeredByControlId={
                            initialTriggeredByControlId
                        }
                        linkedControls={controlResources.controls}
                        linkedControlPrograms={controlResources.controlPrograms}
                    />
                    <Divider />
                    <ControlPlanning
                        id={CONTROL_PLANNING_ANCHOR}
                        titleLabel={anchorLabels[CONTROL_PLANNING_ANCHOR]}
                        control={control}
                        initialCalendars={controlResources.calendars}
                        disabled={disabled}
                    />
                </FormSection>

                <FormSection
                    styleVariant="secondary"
                    id="control-schedule-preview"
                    className={theme.timelineContainer}
                    withDivider
                    stickyContent
                >
                    <ControlFormTimeline control={control} />
                </FormSection>
            </div>
            <FormSection
                id={CONTROL_REMINDERS_ANCHOR}
                titleLabel={anchorLabels[CONTROL_REMINDERS_ANCHOR]}
                withDivider
                tooltip={t("ControlCreate_ModifySchedule_RemindersInfo")}
            >
                <RHFSelectMultipleInput
                    label={t("Control_Reminders_BeforeDue")}
                    placeholder={t("Control_Reminders_Placeholder")}
                    options={options}
                    control={control}
                    name="remindersBefore"
                    nodeAfter={t("Control_Reminders_BeforeDueDate")}
                    disabled={disabled}
                    sortValues={sortRemindersDescending}
                    showSearch
                />
                <RHFSelectMultipleInput
                    label={t("Control_Reminders_AfterDue")}
                    placeholder={t("Control_Reminders_Placeholder")}
                    options={options}
                    control={control}
                    name="remindersAfter"
                    nodeAfter={t("Control_Reminders_AfterDueDate")}
                    disabled={disabled}
                    sortValues={sortRemindersAscending}
                    showSearch
                />
            </FormSection>

            <FormSection
                id={CONTROL_TASKS_ANCHOR}
                titleLabel={anchorLabels[CONTROL_TASKS_ANCHOR]}
            >
                <ControlTasksInput
                    control={control}
                    disabled={disabled}
                    dataSheetAssignments={dataSheetAssignments}
                    controlId={controlId}
                    blobs={mapAsResourceWithoutAction(controlResources.blobs)}
                    onAnalytics={onAnalytics}
                />
            </FormSection>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface ControlProgramLinkProps {
    controlProgramId: number | null;
    options: ControlProgramOption[];
}
function ControlProgramLink({
    controlProgramId,
    options,
}: ControlProgramLinkProps) {
    const { t } = useTranslation();
    if (controlProgramId === null) {
        return null;
    }

    const selectedOption = options.find(
        (option) => option.value === controlProgramId
    );
    if (selectedOption === undefined) {
        const error = new Error(
            `Control program option value ${controlProgramId} not matched with available options`
        );
        console.error(error);
        Sentry.captureException(error);
    }

    return (
        <FormField>
            <FormLabel>
                {t("CreateControl_Control_ControlProgram_Title")}
            </FormLabel>
            <Link href={selectedOption?.url}>
                {selectedOption?.label ??
                    t("CreateControl_Control_ControlProgram_Unknown")}
            </Link>
        </FormField>
    );
}

interface ControlProgramSelectProps {
    control: Control<ControlViewFormSchema>;
    getValues: UseFormGetValues<ControlViewFormSchema>;
    setValue: UseFormSetValue<ControlViewFormSchema>;
    disabled: boolean;
}
function ControlProgramSelect({
    control,
    getValues,
    setValue,
    disabled,
}: ControlProgramSelectProps) {
    const { t } = useTranslation();
    const options = useControlProgramsOptions(disabled ? "View" : "Edit");

    const { field, fieldState } = useController({
        control,
        name: "controlProgramId",
        disabled,
    });

    if (options.status === "loading") {
        return (
            <InputSkeleton
                label={t("CreateControl_Control_ControlProgram_Title")}
            />
        );
    }

    if (disabled) {
        return (
            <ControlProgramLink
                controlProgramId={field.value}
                options={options.data}
            />
        );
    }

    const handleChange = (value: number | null) => {
        field.onChange(value);
        // Clear DataSheet tasks when changing control program
        const responsibleTasks = getValues("responsibleTasks");
        const isDataSheet = (task: (typeof responsibleTasks)[number]) =>
            task.fieldType.type === "DataSheet";
        if (responsibleTasks.some(isDataSheet)) {
            emitNotification(
                "warning",
                t(
                    "CreateControl_Control_ControlProgram_ClearDataSheet_Responsible"
                )
            );
            setValue(
                "responsibleTasks",
                responsibleTasks.filter((task) => !isDataSheet(task)),
                { shouldDirty: true }
            );
        }
    };

    return (
        // Not RHFSelectInput: needs custom onChange to clear DataSheet tasks on program change.
        <SelectInput
            className={theme.controlProgramSelect}
            label={t("CreateControl_Control_ControlProgram_Title")}
            placeholder={t("CreateControl_Control_ControlProgram")}
            disabled={disabled}
            options={options.data}
            onChange={handleChange}
            value={field.value}
            onBlur={field.onBlur}
            error={fieldState.error?.message}
            fieldRef={field.ref}
            filterOption={(input, option) =>
                // AntD types option.label as ReactNode, so narrow to string before matching.
                typeof option?.label === "string" &&
                option.label.toLowerCase().includes(input.toLowerCase())
            }
            showSearch
            dropdownMatchSelectWidth
        />
    );
}

interface ControlDescriptionMarkdownInputProps {
    control: Control<ControlViewFormSchema>;
    setValue: UseFormSetValue<ControlViewFormSchema>;
    styleVariant: "normal" | "borderless";
    disabled: boolean;
}
function ControlDescriptionMarkdownInput({
    control,
    setValue,
    styleVariant,
    disabled,
}: ControlDescriptionMarkdownInputProps) {
    const { t } = useTranslation();
    const sourceToggleMode = useMarkdownSourceToggleMode();

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selection, setSelection] = useState<MarkdownSelectionRange>();
    const markdownEditorRef: RefObject<MarkdownEditorMethods | null> =
        useRef(null);

    const aiControlDescriptionCompletionEnabled = useFeatureFlag(
        "aiControlDescriptionCompletion"
    );

    const onDrawerChange = (isOpen: boolean) => {
        setDrawerOpen(isOpen);
        if (!isOpen) {
            requestAnimationFrame(() => markdownEditorRef.current?.focus());
        }
    };

    return (
        <RHFMarkdownInput
            control={control}
            name="description"
            styleVariant={styleVariant}
            disabled={disabled}
            label={t("CreateControl_Control_Description")}
            placeholder={t("CreateControl_Control_Description_Placeholder")}
            sourceToggleMode={sourceToggleMode}
            editorRef={markdownEditorRef}
            keepEditorOpen={drawerOpen}
            onSelect={setSelection}
            headerAction={
                aiControlDescriptionCompletionEnabled && (
                    <SuggestAiDescriptionButton
                        control={control}
                        setValue={setValue}
                        drawerOpen={drawerOpen}
                        setDrawerOpen={onDrawerChange}
                        editorRef={markdownEditorRef}
                        selection={selection}
                        disabled={disabled}
                    />
                )
            }
        />
    );
}
